import { Client } from "@/Common/Api/Client";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { FileType } from "@/Common/Models/FileType";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseGetExportableFileFetchUrlResponse } from "../BaseGetExportableFileFetchUrlResponse";
import { ExportableFile, IExportableFileLoaded } from "../ExportableFile";
import { GenerateFileOptions } from "../IGenerateFileOptions";
import { DraggableImageOverlayElement } from "../OverlayElements/DraggableImageOverlayElement";
import { OverlayElementType } from "../OverlayElements/OverlayElement";
import { FileTypeTemporaryFile } from "./FileTypeTemporaryFile";

class FileTypeObstetricalFile extends ExportableFile {
    public canGenerateFileXcom: boolean = true;

    public contributionId: string;

    constructor(fields: Partial<FileTypeObstetricalFile>) {
        super(FileType.OBSTETRICALFILE);
        Object.assign(this, fields);
    }

    public getAttachedObject_ObjectId(): string { return this.contributionId; }

    public getCleanCopy(): FileTypeObstetricalFile {
        return new FileTypeObstetricalFile({
            PageNumbers: this.PageNumbers,
            contributionId: this.contributionId,
            date: this.date,
            description: this.description
        });
    }

    public async getMeta(): Promise<number> {
        await this.generateFileIfNeeded({ includeOverlays: true, isExternal: false });
        return this.totalPages;
    }

    protected async generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        return await this.generateFileIfNeeded({ includeOverlays: includeOverlays, isExternal: true });
    }

    protected async generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded> {
        const {
            includeOverlays = true,
            isExternal,
            signPdfFormFromUserPreferences: signFormFromUserPreferences = false,
            includeOverlaysFromPreferences = false
        } = generateFileOption;

        const requestedOverlays = (includeOverlays && this.overlayElements) || [];

        const response = await this.generatePdf(
            this.clientTag,
            this.claimId,
            this.contributionId,
            this.PageNumbers?.sort((a, b) => a - b),
            this.serializeOverlayElements(includeOverlays),
            isExternal,
            signFormFromUserPreferences,
            includeOverlaysFromPreferences
        );

        const temporaryFile = new FileTypeTemporaryFile({
            ObjectId: response?.ObjectId,
            date: this.date,
            description: this.description
        });

        this.totalPages = response?.TotalPages;
        if (response.DraggableImageOverlayElements?.length) {
            const signatures = DraggableImageOverlayElement.fromResponseModelArray(
                response.DraggableImageOverlayElements
            );

            this.applyDistinctOverlayElements(signatures, OverlayElementType.DraggableImage);
        }

        super.setCache(temporaryFile, requestedOverlays);

        return {
            OriginalExportableFile: this,
            PregeneratedTemporaryFile: temporaryFile,
            FetchUrl: isExternal
                ? FileSelectionHelper.getFetchUrlXcom(this.clientTag, this.claimId, response?.ObjectId, temporaryFile.fileType, { noCache: true })
                : FileSelectionHelper.getFetchUrl(response?.ObjectId, temporaryFile.fileType, { noCache: true })
        };
    }

    public async generatePdf(
        clientTag: string,
        claimId: string,
        contributionId: string,
        pageNumbers: number[],
        overlayElements: string[],
        isExternal: boolean,
        signFormFromUserPreferences: boolean = false,
        includeOverlaysFromPreferences: boolean = false
    ): Promise<BaseGetExportableFileFetchUrlResponse> {
        var response =
            isExternal
                ? await Client.postAsyncXcom<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.Myle_ObstetricalFile_GetExportableFile,
                    {
                        ObstetricalFileContributionId: contributionId,
                        PageNumbers: pageNumbers,
                        OverlayElements: overlayElements
                    },
                    clientTag,
                    claimId
                )
                : await Client.postAsync<BaseGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.Myle_ObstetricalFile_GetExportableFile,
                    {
                        ObstetricalFileContributionId: contributionId,
                        PageNumbers: pageNumbers,
                        OverlayElements: overlayElements,
                        SignFormFromUserPreferences: !!signFormFromUserPreferences,
                        IncludeOverlaysFromPreferences: !!includeOverlaysFromPreferences

                    }
                );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }
}

export { FileTypeObstetricalFile };
